"use client";

import {
  Handle,
  NodeToolbar,
  Position,
  useStore,
  useUpdateNodeInternals,
  type NodeProps,
} from "@xyflow/react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useModKey } from "@/lib/use-shift-key";
import { useSystemStore } from "@/lib/store";
import { useCanvasHover } from "@/lib/canvas-hover";
import { useCanvasBlueprint, useCanvasViewOnly } from "./canvas-mode";
import { MUTED_CABLE_COLOR, resolveJunctionColor } from "@/lib/cable-color";
import { GEOMETRY } from "@/lib/canvas-geometry";
import { indexNodesById } from "@/lib/node-tree";
import { KeystrokeHint, MOD_LABEL } from "@/components/ui/keystroke-hint";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";
import { useT } from "@/lib/i18n";
import { MobileDetailsPill } from "./mobile-details-pill";

const SIZE = GEOMETRY.JUNCTION_HALF * 2;

const SIDES = [
  { id: "top", position: Position.Top },
  { id: "right", position: Position.Right },
  { id: "bottom", position: Position.Bottom },
  { id: "left", position: Position.Left },
] as const;

function JunctionNodeInner({ id, selected, data }: NodeProps) {
  const tr = useT();
  const cableTarget = (data as { cableTarget?: boolean } | undefined)?.cableTarget;
  const provisional = (data as { provisional?: boolean } | undefined)?.provisional;
  const blueprint = useCanvasBlueprint();
  const modHeld = useModKey();
  const viewOnly = useCanvasViewOnly();
  const isTouchLayout = useIsTouchLayout();
  const zoom = useStore((state) => state.transform[2]);
  const updateNodeInternals = useUpdateNodeInternals();
  const [hovered, setHovered] = useState(false);
  const nodes = useSystemStore((s) => s.system.nodes);
  const cables = useSystemStore((s) => s.system.cables);
  const dotColor = useMemo(() => {
    const c = resolveJunctionColor(id, indexNodesById(nodes), cables);
    return c === MUTED_CABLE_COLOR ? "var(--color-fg)" : c;
  }, [id, nodes, cables]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const setHoverNodeId = useCanvasHover((s) => s.setHoverNodeId);

  // Desktop keeps the modifier gesture. Touch gets a separate branch handle,
  // so selecting the dot never makes the junction itself impossible to move.
  const wireMode = modHeld && !viewOnly && !blueprint;
  const showTouchBranch =
    isTouchLayout && selected && !viewOnly && !blueprint;
  const showTouchDetails = isTouchLayout && selected && !blueprint;
  const safeZoom = Math.max(zoom, 0.01);

  useEffect(() => {
    if (!showTouchBranch) return;
    // The branch control is inversely sized in flow space. Re-measure its
    // registered handle whenever zoom changes so a new cable starts at the
    // visible plus button rather than a stale bound.
    updateNodeInternals(id);
  }, [id, safeZoom, showTouchBranch, updateNodeInternals]);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: SIZE,
        height: SIZE,
        position: "relative",
        cursor: blueprint
          ? "default"
          : viewOnly
            ? "pointer"
            : wireMode
              ? "crosshair"
              : "grab",
      }}
      onMouseEnter={
        blueprint
          ? undefined
          : () => {
              setHovered(true);
              setHoverNodeId(id);
            }
      }
      onMouseLeave={
        blueprint
          ? undefined
          : () => {
              setHovered(false);
              setHoverNodeId(
                useCanvasHover.getState().hoverNodeId === id
                  ? null
                  : useCanvasHover.getState().hoverNodeId,
              );
            }
      }
    >
      {/* Hit overlay overflows the dot without changing the measured bounds JUNCTION_HALF assumes. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: -7,
          pointerEvents: blueprint ? "none" : wireMode ? "none" : "auto",
          cursor: blueprint ? "default" : "grab",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 2,
          background:
            selected || cableTarget || wireMode
              ? "var(--color-accent)"
              : provisional
                ? "transparent"
                : "var(--color-surface)",
          border: `1.5px solid ${
            selected || wireMode || cableTarget ? "var(--color-accent)" : dotColor === "#ffffff" ? "var(--color-fg-muted)" : dotColor
          }`,
          borderRadius: 999,
          boxShadow: cableTarget
            ? "0 0 0 4px hsl(214 88% 52% / 0.28)"
            : selected
              ? "0 0 0 3px hsl(214 88% 52% / 0.18)"
              : hovered
                ? "0 0 0 2.5px hsl(214 88% 52% / 0.18)"
                : undefined,
          transition: "background 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
          pointerEvents: "none",
        }}
      />
      {/* Stacked at centre; `rfEdges` renders on whichever cardinal matches the cable's approach. */}
      {SIDES.map((s) => (
        <Handle
          key={s.id}
          id={s.id}
          type="source"
          position={s.position}
          isConnectable={!viewOnly}
          style={{
            width: SIZE,
            height: SIZE,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            background: "transparent",
            border: "none",
            borderRadius: 999,
            pointerEvents: wireMode ? "auto" : "none",
          }}
        />
      ))}
      {showTouchBranch && (
        <Handle
          id="touch-branch"
          type="source"
          position={Position.Right}
          isConnectable
          aria-label={tr("mobile.junction.branch")}
          role="button"
          tabIndex={0}
          className="nodrag touch-control-handle"
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.currentTarget.click();
          }}
          style={{
            width: 44 / safeZoom,
            height: 44 / safeZoom,
            left: SIZE + 28 / safeZoom,
            top: "50%",
            transform: "translate(-50%, -50%)",
            background: "var(--color-surface)",
            border: `${1.5 / safeZoom}px solid var(--color-accent)`,
            color: "var(--color-accent)",
            fontSize: 20 / safeZoom,
            lineHeight: 1,
            pointerEvents: "auto",
          }}
        >
          +
        </Handle>
      )}
      {showTouchDetails && (
        <NodeToolbar
          nodeId={id}
          isVisible
          position={Position.Bottom}
          offset={28}
          className="nodrag nopan"
          style={{ pointerEvents: "auto" }}
        >
          <MobileDetailsPill anchor={{ kind: "node", id }} />
        </NodeToolbar>
      )}
      {/* Every stored junction cable references this handle id. */}
      <Handle
        id="j"
        type="source"
        position={Position.Right}
        isConnectable={!viewOnly}
        style={{
          width: 1,
          height: 1,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          background: "transparent",
          border: "none",
          pointerEvents: "none",
          opacity: 0,
        }}
      />
      <KeystrokeHint
        anchorRef={wrapperRef}
        show={hovered && !viewOnly && !blueprint}
        active={wireMode}
        label={wireMode ? "drag to add cable" : `${MOD_LABEL} + drag to add cable`}
      />
    </div>
  );
}

export const JunctionNodeView = memo(JunctionNodeInner);
